// Builder Discovery Service - Use-case risk discovery for Builder assessments
// Uses Builder-specific endpoints and Redux state (builderRisks, not explorerRisks or modelRisks)
import {
  addBuilderRisksBatch,
  updateBuilderAreaStatus,
  setBuilderAreaAnalysis,
  BUILDER_RISK_AREAS
} from '../../redux/slices/risksSlice';
import { v4 as uuid } from 'uuid';
import { authFetch } from '../../utils/authFetch';

export class BuilderDiscoveryService {
  constructor(dispatch, modelId, assessmentUuid, parentAssessmentUuid, businessContext, user, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.parentAssessmentUuid = parentAssessmentUuid;
    this.businessContext = businessContext;
    this.user = user;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeDiscovery(areaKey, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, inheritedRisks, progressCallback) {
    try {
      this.dispatch(updateBuilderAreaStatus({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        status: 'in_progress'
      }));

      // Task 1: Analyzing Risk Area (Builder-specific)
      if (progressCallback) progressCallback('analyzing risk area');
      await new Promise(resolve => setTimeout(resolve, 500));

      const areaRes = await authFetch('/.netlify/functions/builderAnalyzeRiskArea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId: this.modelId,
          assessmentUuid: this.assessmentUuid,
          parentAssessmentUuid: this.parentAssessmentUuid,
          areaKey,
          areaDefinition,
          contextSummaries: useCaseAnalysisData,
          businessContext: this.businessContext,
          inheritedRisks: inheritedRisks
        })
      });

      if (!areaRes.ok) throw new Error(await areaRes.text().catch(() => 'Area analysis failed'));
      const { areaCard } = await areaRes.json();

      this.dispatch(setBuilderAreaAnalysis({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        analysis: {
          summary: areaCard?.summary || [],
          analysis: areaCard?.analysis || ''
        }
      }));

      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('area', { areaCard, areaKey });
      }

      // Task 2: Researching Risk Sources (Builder-specific)
      if (progressCallback) progressCallback('researching risk sources');
      await new Promise(resolve => setTimeout(resolve, 800));

      const sourcesRes = await authFetch('/.netlify/functions/builderResearchRiskSources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId: this.modelId,
          assessmentUuid: this.assessmentUuid,
          parentAssessmentUuid: this.parentAssessmentUuid,
          areaKey,
          areaDefinition,
          contextSummaries: useCaseAnalysisData,
          businessContext: this.businessContext,
          inheritedRisks: inheritedRisks
        })
      });

      if (!sourcesRes.ok) throw new Error(await sourcesRes.text().catch(() => 'Source research failed'));
      const { selectedSources } = await sourcesRes.json();

      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('sources', { selectedSources, areaKey });
      }

      // Task 3: Researching Root Causes
      if (progressCallback) progressCallback('researching root causes');
      await new Promise(resolve => setTimeout(resolve, 800));

      let causesBySource = {};
      try {
        const causesRes = await authFetch('/.netlify/functions/builderResearchRootCauses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            modelId: this.modelId,
            assessmentUuid: this.assessmentUuid,
            parentAssessmentUuid: this.parentAssessmentUuid,
            areaKey,
            areaDefinition,
            selectedSources,
            contextSummaries: useCaseAnalysisData,
            businessContext: this.businessContext,
            evaluationCriteria: riskCauseEvalCfg,
            inheritedRisks: inheritedRisks
          })
        });

        if (causesRes.ok) {
          const data = await causesRes.json();
          causesBySource = data.causesBySource || {};
        } else {
          console.warn('[BuilderDiscoveryService] Root cause research failed, using fallback approach');
          causesBySource = this.generateFallbackCauses(selectedSources, areaDefinition);
        }
      } catch (causesError) {
        console.warn('[BuilderDiscoveryService] Root cause research timed out, using fallback approach:', causesError.message);
        causesBySource = this.generateFallbackCauses(selectedSources, areaDefinition);
      }

      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('causes', { causesBySource, areaKey });
      }

      // Task 4: Extract Relevant Causes for RAG
      if (progressCallback) progressCallback('preparing causes for RAG');
      await new Promise(resolve => setTimeout(resolve, 200));

      const relevantCausesForRAG = this.extractRelevantCausesForRAG(selectedSources, causesBySource, useCaseAnalysisData);

      console.log(`[BuilderDiscoveryService] Extracted ${relevantCausesForRAG.length} relevant causes for RAG enhancement`);

      if (this.liveUpdateCallback) {
        this.liveUpdateCallback('causesForRAG', {
          relevantCauses: relevantCausesForRAG,
          totalCauseCategories: Object.keys(causesBySource).length,
          areaKey
        });
      }

      // Task 5: RAG-Powered Scenarios (Builder-specific)
      if (progressCallback) progressCallback('scenarios');
      await new Promise(resolve => setTimeout(resolve, 1500));

      let scenarios = [];
      let ragMetadata = {};
      try {
        console.log(`[BuilderDiscoveryService] SENDING TO RAG: ${relevantCausesForRAG.length} causes`);

        const scenRes = await authFetch('/.netlify/functions/builderGenerateScenariosWithRAG', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            card: areaCard,
            modelNarratives: useCaseAnalysisData,
            areaKey,
            areaDefinition,
            selectedCatalogues: [],
            useAllCatalogues: true,
            selectedSources: selectedSources,
            relevantCauses: relevantCausesForRAG,
            contextEnrichment: true,
            businessContext: this.businessContext,
            inheritedRisks: inheritedRisks,
            assessmentUuid: this.assessmentUuid,         // backend looks up applied_register_ids
            parentAssessmentUuid: this.parentAssessmentUuid
          })
        });

        if (scenRes.ok) {
          const scenData = await scenRes.json();
          scenarios = scenData.scenarios || [];

          ragMetadata = {
            method: scenData.method || 'RAG',
            cataloguesUsed: scenData.cataloguesUsed || [],
            retrievedRisks: scenData.retrievedRisks || [],
            generationSource: 'Builder-RAG-Enhanced'
          };

          console.log(`[BuilderDiscoveryService] Auto-promoting ${scenarios.length} scenarios to Builder risks`);
          const promotionStartTime = Date.now();

          const risksToAdd = scenarios.map((scenario, index) => {
            const riskId = uuid();
            const userSourceName = selectedSources?.[index % selectedSources.length]?.sourceName || 'Generated Risk Source';

            return {
              category: userSourceName,
              risk: {
                riskId,
                riskName: scenario.scenarioText || scenario.title || 'Generated Risk Scenario',
                rationale: scenario.rationale || scenario.evidence || '',
                status: 'Saved',
                generationMethod: ragMetadata.method,
                cataloguesUsed: ragMetadata.cataloguesUsed,
                entity: scenario.entity,
                intent: scenario.intent,
                timing: scenario.timing,
                other: scenario.other,
                confidence: scenario.confidence,
                areaKey,
                type: 'Builder'
              }
            };
          });

          if (risksToAdd.length > 0) {
            this.dispatch(addBuilderRisksBatch({
              assessmentUuid: this.assessmentUuid,
              areaKey,
              risks: risksToAdd
            }));

            await this.saveRisksToDatabase(risksToAdd, areaKey);
          }

          const promotionDuration = Date.now() - promotionStartTime;
          console.log(`[BuilderDiscoveryService] All ${risksToAdd.length} scenarios promoted in ${promotionDuration}ms`);

          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('scenarios', {
              scenarios,
              ragMetadata: {
                ...ragMetadata,
                inputCauses: relevantCausesForRAG.length,
                catalogueCount: ragMetadata.cataloguesUsed?.length || 0,
                catalogueNames: ragMetadata.cataloguesUsed || [],
                retrievedRisksCount: ragMetadata.retrievedRisks?.length || 0,
                generationSummary: `Generated ${scenarios.length} use-case risk entries from ${relevantCausesForRAG.length} causes`
              },
              totalGenerated: scenarios.length,
              totalPromoted: risksToAdd.length,
              areaKey
            });
          }
        }
      } catch (scenError) {
        console.warn('[BuilderDiscoveryService] RAG scenario generation failed:', scenError);
      }

      this.dispatch(updateBuilderAreaStatus({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        status: 'completed'
      }));

      return {
        areaCard,
        selectedSources,
        causesBySource,
        relevantCausesForRAG,
        scenarios,
        ragMetadata,
        areaKey,
        version: 'navigator_v1'
      };

    } catch (error) {
      console.error('[BuilderDiscoveryService] Fatal error:', error);

      this.dispatch(updateBuilderAreaStatus({
        assessmentUuid: this.assessmentUuid,
        areaKey,
        status: 'error'
      }));

      throw error;
    }
  }

  async retryArea(areaKey, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, inheritedRisks) {
    console.log(`[BuilderDiscoveryService] Retrying area: ${areaKey}`);
    return this.executeDiscovery(areaKey, areaDefinition, useCaseAnalysisData, riskCauseEvalCfg, inheritedRisks, null);
  }

  generateFallbackCauses(selectedSources, areaDefinition) {
    const causesBySource = {};

    try {
      const riskSources = Array.isArray(areaDefinition?.riskSources) ? areaDefinition.riskSources : [];

      selectedSources.forEach(selectedSource => {
        const sourceDef = riskSources.find(rs => rs.name === selectedSource.sourceName);
        if (sourceDef?.causes) {
          const causes = [];
          Object.entries(sourceDef.causes).forEach(([category, categoryData]) => {
            const options = Array.isArray(categoryData.options) ? categoryData.options : [];
            options.slice(0, 3).forEach(option => {
              causes.push({
                name: option,
                evidence: `Potential use-case cause identified for ${selectedSource.sourceName}`,
                confidence: 0.7,
                category: category
              });
            });
          });

          if (causes.length > 0) {
            causesBySource[selectedSource.sourceName] = causes;
          }
        }
      });

      console.log(`[BuilderDiscoveryService] Generated fallback causes for ${Object.keys(causesBySource).length} sources`);
    } catch (error) {
      console.error('[BuilderDiscoveryService] Error generating fallback causes:', error);
    }

    return causesBySource;
  }

  extractRelevantCausesForRAG(selectedSources, causesBySource, useCaseContext) {
    const relevantCauses = [];

    try {
      selectedSources.forEach(source => {
        const sourceCauseData = causesBySource[source.sourceName];

        if (Array.isArray(sourceCauseData)) {
          sourceCauseData.forEach(categoryResult => {
            if (categoryResult.researchedCauses && categoryResult.researchedCauses.length > 0) {
              categoryResult.researchedCauses.forEach(cause => {
                if (cause.confidence >= 0.4) {
                  const evaluationContext = Object.keys(cause)
                    .filter(key => !['name', 'description', 'evidence', 'confidence'].includes(key))
                    .map(key => `${key}: ${cause[key]}`)
                    .join('/');

                  relevantCauses.push({
                    source: source.sourceName,
                    sourceConfidence: source.aiConfidence,
                    causeName: cause.name,
                    description: cause.description,
                    evidence: cause.evidence,
                    confidence: cause.confidence,
                    category: categoryResult.category,
                    searchContext: `${source.sourceName} ${categoryResult.category}: ${cause.name}${evaluationContext ? ` (${evaluationContext})` : ''} - ${cause.evidence}`
                  });
                }
              });
            }
          });
        }
      });

      relevantCauses.sort((a, b) =>
        (b.sourceConfidence * b.confidence) - (a.sourceConfidence * a.confidence)
      );

      return relevantCauses.slice(0, 10);
    } catch (error) {
      console.error('[BuilderDiscoveryService] Error extracting relevant causes for RAG:', error);
      return [];
    }
  }

  async saveRisksToDatabase(risksToAdd, areaKey) {
    try {
      const savePromises = risksToAdd.map(riskItem => {
        const { category, risk } = riskItem;
        return authFetch('/.netlify/functions/saveRisk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            riskId: risk.riskId,
            name: risk.riskName,
            section: areaKey,
            category: category,
            type: 'Builder',
            rag_source_file: null,
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            owner: {
              name: this.user?.name || 'Unknown User',
              email: this.user?.email || 'unknown@email.com',
              picture: this.user?.picture || null
            }
          })
        });
      });

      const responses = await Promise.all(savePromises);
      const successCount = responses.filter(r => r.ok).length;
      console.log(`[BuilderDiscoveryService] Saved ${successCount}/${risksToAdd.length} risks to database for area ${areaKey}`);

      if (successCount < risksToAdd.length) {
        console.warn(`[BuilderDiscoveryService] Failed to save ${risksToAdd.length - successCount} risks to database`);
      }
    } catch (error) {
      console.error('[BuilderDiscoveryService] Error saving risks to database:', error);
    }
  }

  static isValidBuilderArea(areaKey) {
    return BUILDER_RISK_AREAS.includes(areaKey);
  }

  static getBuilderAreas() {
    return BUILDER_RISK_AREAS;
  }
}
